export const concepts = [
    // 1. Architecture & Scaling
    {
        id: 'cap-theorem',
        category: 'Architecture & Scaling',
        title: 'CAP Theorem',
        summary: 'The fundamental trade-off in distributed systems: Consistency, Availability, Partition Tolerance.',
        content: `
## What is the CAP Theorem?

The CAP Theorem, informally known as Brewer's theorem, is an indispensable concept for understanding how distributed data stores behave in the face of network failures. It states that any distributed system can only guarantee **two out of the three** following characteristics simultaneously:

1. **Consistency (C):** Every read operation receives the most recent write or an error. In other words, all nodes in the distributed system see the exact same data at the exact same time. If a financial transaction updates an account balance, the very next read on any node will reflect that updated balance.
2. **Availability (A):** Every request receives a (non-error) response, without the guarantee that the response contains the most recent write. The system remains operational and responsive.
3. **Partition Tolerance (P):** The system continues to operate despite an arbitrary number of messages being dropped, delayed, or lost by the network connecting the nodes.

### The Reality of Modern Networks

In real-world distributed systems deployed across data centers and the cloud, **network partitions are an absolute certainty**. A fiber optic cable can be severed, a network switch can fail, or a routing misconfiguration can isolate an entire availability zone.

Because **Partition Tolerance (P) is a necessity, not a choice**, system architects must decide between **Consistency** and **Availability** when a partition happens. You do not choose between CP, AP, or CA; you choose what happens *during* a partition: CP or AP.

#### CP Systems (Consistency & Partition Tolerance)
If the system dictates strict consistency, it will return an error or time out if it cannot guarantee up-to-date data. It chooses to stop serving requests rather than serve stale data.
- *Mechanism:* During a partition, minority nodes will reject writes and reads to prevent split-brain scenarios until a quorum can be re-established.
- *Examples:* MongoDB, Redis (in cluster mode), HBase, etcd, Apache ZooKeeper.
- *Use Cases:* Financial systems, banking, inventory management, distributed locking mechanisms—any domain where reading stale data is catastrophic.

#### AP Systems (Availability & Partition Tolerance)
If the system prioritizes availability, it will return the most recent version of the data it has, even if that data might be stale. Once the partition resolves, the nodes sync up their data (this is known as Eventual Consistency).
- *Mechanism:* Nodes continue to accept reads and writes, even if they cannot communicate with the rest of the cluster. Conflict resolution happens after the partition heals.
- *Examples:* Apache Cassandra, Amazon DynamoDB, CouchDB, Riak.
- *Use Cases:* Social media feeds, product reviews, shopping carts, analytics tracking—domains where high uptime is critical and users won't notice if data is a few seconds behind.

### CAP in Action: A Simple Analogy
Imagine a two-branch bank (Node A and Node B). If the phone line between them goes down (a Network Partition):
- **CP Choice:** You close both bank branches (Unavailable) so nobody can overdraw their account (Consistent).
- **AP Choice:** You let people withdraw money from both branches (Available), taking the risk that someone might overdraw, and you reconcile the accounts later (Eventually Consistent).

<diagram>
graph TD
    A[Consistency] --- B
    B[Partition Tolerance] --- C
    C[Availability] --- A
    classDef highlight fill:#f9f9f9,stroke:#333,stroke-width:2px;
    A:::highlight
    B:::highlight
    C:::highlight
</diagram>

---

> **⚡ Quick Look:**
> - **C:** Everyone sees the same data at the same time.
> - **A:** The system always responds (no timeouts).
> - **P:** The network can fail, and the system survives.
> - *The golden rule:* Because 'P' is unavoidable in the real world, you must choose between 'C' (cancel the request) or 'A' (serve stale data) during an outage.
    `
    },
    {
        id: 'pacelc-theorem',
        category: 'Architecture & Scaling',
        title: 'PACELC Theorem',
        summary: 'An extension to CAP theorem addressing trade-offs during normal operations.',
        content: `
## Beyond CAP: PACELC Theorem

While the CAP theorem is essential, it paints an incomplete picture. It only explains the trade-off *during a network partition* (which is an exceptional state). But what happens 99.9% of the time when the network is running perfectly fine? 

PACELC (formulated by Daniel Abadi) extends CAP by adding another crucial dimension: the trade-off during normal operations.

**P A C E L C** stands for:
**I**f there is a **P**artition (**P**), how does the system trade off **A**vailability and **C**onsistency (**A** vs **C**)?
**E**lse (**E**), when the system is running normally without partitions, how does it trade off **L**atency and **C**onsistency (**L** vs **C**)?

### Breaking Down PACELC

- **P + A/C:** The exact same dilemma as the CAP theorem. Under a network partition, do we choose Availability or Consistency?
- **E + L/C:** Under normal conditions (Else), do we choose Latency (return data instantly, but it might be slightly stale because nodes haven't finished background syncing) or Consistency (wait to respond until all nodes in the cluster have acknowledged the write, ensuring perfect consistency but suffering high latency)?

### Real World Database Classification

1. **PA/EL Systems (e.g., DynamoDB, Cassandra, Riak):**
   - **PA:** Under partition, they remain Highly Available.
   - **EL:** Under normal conditions, they prioritize extremely low Latency over strict consistency by returning data before all replicas have synced.

2. **PC/EC Systems (e.g., MongoDB, HBase, Relational DBs):**
   - **PC:** Under partition, they choose strict Consistency (failing requests if quorum is lost).
   - **EC:** Under normal conditions, they also choose Consistency (syncing data across replicas before confirming the write to the client, accepting higher latency).

3. **PC/EL Systems (e.g., PNUTS by Yahoo):**
   - Chooses consistency under partition (fails safe), but prioritizes latency under normal operations for faster read/write speeds.

4. **PA/EC Systems**:
   - Rarely seen in practice. It implies prioritizing availability during an emergency, but accepting slow, strictly consistent performance during normal operations.

### Why PACELC Matters for Interviews
System design interviews often push you on "what happens if?" tradeoffs. Saying "We use Cassandra because of CAP" is a junior response. Saying "We use Cassandra because it is a PA/EL system, meaning we prioritize sub-millisecond latency reads/writes during normal operations, and remain highly available during network failures, making it perfect for our high-throughput activity logs" demonstrates Senior-level architectural depth.

<diagram>
graph TD
    A{Is there a Network Partition?}
    A -->|Yes (P)| B{Choose one}
    B -->|A| C[Availability <br/> Cassandra / DynamoDB]
    B -->|C| D[Consistency <br/> MongoDB / HBase]
    
    A -->|No (E - Else)| F{Choose one}
    F -->|L| G[Latency <br/> Cassandra / DynamoDB]
    F -->|C| H[Consistency <br/> MongoDB / Relational]
</diagram>

---

> **⚡ Quick Look:**
> - **CAP:** Explains behavior *during* a network outage (Availability vs. Consistency).
> - **PACELC:** Explains behavior *always*. During an outage (A vs C), **E**lse during normal operations (Latency vs Consistency).
> - *Interview Tip:* Use PACELC to justify why you chose a NoSQL DB for speed (EL), or an RDBMS for safety (EC).
    `
    },
    {
        id: 'scaling',
        category: 'Architecture & Scaling',
        title: 'Horizontal vs Vertical Scaling',
        summary: 'The difference between adding more power to a machine vs adding more machines to a pool.',
        content: `
## Scaling: Vertical vs Horizontal

When a system surpasses its capacity—whether due to CPU starvation, memory exhaustion, or network limits—you must scale it out. There are two fundamental architectural patterns to achieve this.

### Vertical Scaling (Scaling Up)
Vertical scaling involves adding more physical resources (CPU cores, RAM size, Disk I/O throughput) to an existing single machine. It's like upgrading the engine in a single delivery truck instead of buying a fleet.

- **Pros:** 
  1. **Extreme Simplicity:** No application code changes are needed. The app simply has more breathing room.
  2. **No Network Overhead:** Because everything stays on one machine, there are zero inter-node communication delays or distributed data consistency issues.
  3. **Easier Maintenance:** You only manage one gigantic server.
- **Cons:** 
  1. **Hard Physical Limits:** A single server can only get so big before it becomes impossibly expensive or physically impossible to upgrade further.
  2. **Downtime:** Requires taking the server offline to upgrade the hardware or re-provision the VM instance.
  3. **Single Point of Failure (SPOF):** If the motherboard burns out on your monolithic server, your entire application goes offline instantly.
- *Ideal Use Cases:* Very early-stage startups, simple relational databases where the complexity of sharding outweighs the cost of a beefier server.

### Horizontal Scaling (Scaling Out)
Horizontal scaling involves adding more machines to your resource pool to share the processing and memory load. It's like buying 10 smaller delivery trucks instead of one massive semi-truck.

- **Pros:** 
  1. **Virtually Infinite Scale:** You can theoretically add an infinite number of cheap, commodity cloud servers to handle unbounded traffic.
  2. **High Availability & Fault Tolerance:** If Node A catches fire, the Load Balancer seamlessly routes traffic to Nodes B, C, and D. The user experiences zero downtime.
  3. **Cost Effective:** 10 cheap servers often cost less than 1 elite super-server.
- **Cons:** 
  1. **High Complexity:** You must introduce Load Balancers.
  2. **Statelessness Required:** The application must be stateless. You cannot store user sessions in local server memory; they must be moved to an external cache (like Redis).
  3. **Data Splitting:** Databases must be scaled horizontally via complex techniques like Sharding or Replication.
- *Ideal Use Cases:* Modern cloud web applications, microservices, stateless API layers, and highly distributed NoSQL databases.

<diagram>
graph LR
    subgraph Vertical Scaling
    A[Small Server] --> B[Giant Server]
    end
    subgraph Horizontal Scaling
    C[Small Server] --> D[Load Balancer]
    D --> E[Server 1]
    D --> F[Server 2]
    D --> G[Server 3]
    end
</diagram>

---

> **⚡ Quick Look:**
> - **Vertical (Scale Up):** Buying a bigger machine. Fast to implement, has a hard ceiling, risk of single-point-of-failure.
> - **Horizontal (Scale Out):** Buying more machines. Infinite scale, highly resilient, but requires load balancers and complex distributed state management.
    `
    },
    {
        id: 'microservices',
        category: 'Architecture & Scaling',
        title: 'Monolith vs Microservices',
        summary: 'Architectural patterns: single unified codebase vs broken-down independent services.',
        content: `
## Monolithic vs Microservice Architecture

The debate between building a monolith versus breaking it into microservices is the defining architectural decision for any application.

### Monolithic Architecture
A monolithic application is built as a single, indivisible, unified unit. The user interface, business logic, data access layer, and background jobs are all packaged and deployed together.

- **Pros:** 
  1. **Simple to Develop:** Everything is in one repository. Any developer can easily trace the code.
  2. **Simple Testing:** You can launch the entire application with one command and run end-to-end UI and integration tests easily.
  3. **Simple to Deploy:** Just drop a single compiled binary, WAR file, or Docker container onto a server, and you're done.
  4. **Performance:** In-memory method calls are inherently faster than network-bound RPC/HTTP calls.
- **Cons:** 
  1. **The "Big Ball of Mud":** As the codebase grows over years with multiple teams, it becomes tangled. Changing the "Notification" code might accidentally break the "Checkout" code.
  2. **Slow Deployments:** A tiny typo fix requires compiling and deploying the entire multi-gigabyte application.
  3. **Scaling Bottlenecks:** If only the Image Processing module is experiencing heavy load, you are forced to scale the *entire* application just to support that one module.

### Microservices Architecture
The application is broken down into a collection of smaller, completely independent services, usually organized around business capabilities (e.g., Billing Service, User Auth Service, Inventory Service). Each service has its own database and communicates via network APIs.

- **Pros:** 
  1. **Independent Deployments:** You can deploy an update to the Billing Service 50 times a day without ever risking the Inventory Service going down.
  2. **Granular Scalability:** If Image Processing is under heavy load, you can spin up 100 instances of the Image Processing Service, while leaving the Auth Service at 2 instances.
  3. **Technology Diversity:** The Machine Learning team can write their microservice in Python, the backend engineers can use Go, and the frontend data aggregation layer can use Node.js.
  4. **Fault Isolation:** A memory leak crashing the PDF Generation service doesn't take down the core application.
- **Cons:** 
  1. **Distributed System Complexity:** Network calls *will* fail. You must handle retries, timeouts, jitter, and Circuit Breakers.
  2. **Data Consistency:** Because microservices don't share a database, complex distributed transactions (like the Saga Pattern or Two-Phase Commits) replace simple, safe SQL JOINs.
  3. **Operational Overhead:** Managing 50 individual services requires an elite DevOps team, Kubernetes, Service Meshes, centralized logging (ELK), and distributed tracing (Jaeger).

<diagram>
graph TD
    subgraph Monolith
    UI1[Web UI] --> AppLayer[Huge Backend Codebase]
    AppLayer --> DB1[(Single Shared Database)]
    end
    
    subgraph Microservices
    UI2[Web UI] --> API[API Gateway]
    API --> SvcA[User Service]
    API --> SvcB[Billing Service]
    API --> SvcC[Inventory Svc]
    SvcA --> DBA[(User DB)]
    SvcB --> DBB[(Billing DB)]
    SvcC --> DBC[(Inventory DB)]
    end
</diagram>

---

> **⚡ Quick Look:**
> - **Monolith:** One massive codebase. Easy at first, becomes a nightmare to maintain and scale as the team grows past 20 engineers.
> - **Microservices:** Dozens of tiny applications talking over the network. Allows massive teams to move fast independently, but introduces brutal network and distributed data complexities.
    `
    },

    // 2. Networking
    {
        id: 'dns',
        category: 'Networking',
        title: 'Domain Name System (DNS)',
        summary: 'The phonebook of the internet, translating domain names to IP addresses.',
        content: `
## Domain Name System (DNS)

DNS is literally the phonebook of the internet. Humans remember names (like \`google.com\`), but computers route traffic based on IP addresses (like \`142.250.190.46\`). DNS performs the critical translation between the two.

### The Multi-Step DNS Resolution Process
When you type a URL into your browser, it doesn't just magically connect. A highly orchestrated, heavily cached lookup occurs:

1. **Browser & OS Cache:** The browser checks its own internal DNS cache. If missed, it queries the operating system's DNS cache.
2. **Recursive Resolver:** Provided usually by your ISP (or services like Cloudflare 1.1.1.1 or Google 8.8.8.8). It performs the heavy lifting, traversing the global DNS tree on your behalf.
3. **Root Nameserver:** There are 13 logical root nameservers globally. The Resolver asks the Root, "Where is the TLD server for \`.com\`?"
4. **TLD Nameserver (Top-Level Domain):** The Resolver asks the \`.com\` TLD server, "Where is the authoritative server for \`amazon.com\`?"
5. **Authoritative Nameserver:** The final destination (managed by AWS Route53 or Cloudflare). It holds the actual DNS records and returns the precise IP address mapping.

### Critical DNS Record Types
- **A Record:** Maps a domain name directly to an IPv4 address.
- **AAAA Record:** Maps a domain name to an IPv6 address.
- **CNAME (Canonical Name):** Maps a domain name to another domain name (an alias). \`www.google.com\` might be a CNAME pointing to \`google.com\`.
- **MX Record:** Directs email traffic to a specific mail server.
- **TXT Record:** Arbitrary text often used for domain verification and email security (SPF, DKIM).

### DNS in System Design
In massive system design architecture, DNS isn't just a phonebook; it is the **very first layer of Load Balancing**.
- **DNS Round Robin:** The Authoritative server returns a list of 5 IP addresses, rotating the order so traffic is pseudo-randomly distributed across 5 edge servers.
- **Geo-DNS (Latency-Based Routing):** Advanced DNS services (like Route53) check the IP address of the user making the request, and return the IP address of the data center geographically closest to them, slashing network transit latency.

<diagram>
sequenceDiagram
    participant User
    participant Browser Cache
    participant OS Cache
    participant ISP Resolver
    participant Root Server
    participant TLD Server
    participant Auth Server
    
    User->>Browser Cache: google.com
    Browser Cache-->>User: Miss
    User->>OS Cache: google.com
    OS Cache-->>User: Miss
    User->>ISP Resolver: Where is google.com?
    ISP Resolver->>Root Server: Where is .com?
    Root Server-->>ISP Resolver: Ask TLD (.com)
    ISP Resolver->>TLD Server: Where is google.com?
    TLD Server-->>ISP Resolver: Ask Auth (ns1.google.com)
    ISP Resolver->>Auth Server: What is IP for google.com?
    Auth Server-->>ISP Resolver: 142.250.190.46
    ISP Resolver-->>User: 142.250.190.46
</diagram>

---

> **⚡ Quick Look:**
> - **Function:** Translates human domains to computer IPs.
> - **Flow:** Browser Cache -> OS Cache -> Recursive Resolver -> Root Server -> TLD Server -> Authoritative Server.
> - **System Design Tip:** Use DNS Geo-Routing to route users to the nearest data center globally, providing "Level 0" load balancing.
    `
    },
    {
        id: 'load-balancing',
        category: 'Networking',
        title: 'Load Balancing',
        summary: 'Techniques and algorithms to distribute incoming network traffic across multiple servers.',
        content: `
## Load Balancing Deep Dive

A Load Balancer acts as the centralized "traffic cop" sitting directly in front of your application servers. Its job is to ingest massive amounts of inbound client traffic and route it predictably across a fleet of backend servers.

### Why is Load Balancing Mandatory?
1. **Redundancy:** If Server A catches fire, the Load Balancer instantly detects the failure (via Health Checks) and reroutes traffic to Servers B and C. 
2. **Scalability:** You can transparently add 100 more servers behind the Load Balancer during a Super Bowl spike, and clients never need to know the IPs.
3. **Performance:** Distributes CPU and memory load evenly so no single server bottlenecks the application.

### L4 vs. L7 Load Balancing
Load balancers operate at different layers of the OSI networking model, offering distinct trade-offs between speed and intelligence.

#### Layer 4 Load Balancing (Transport Layer)
- **How it works:** Routes traffic based purely on network-level data (Source/Destination IP and TCP/UDP ports).
- *Pros:* Blazing fast. Uses almost zero compute because it doesn't open the "envelope" to inspect the message. Can handle millions of concurrent connections easily.
- *Cons:* "Dumb" routing. It cannot inspect the content of an HTTP request (no URL paths, no cookies, no headers).
- *Example:* AWS Network Load Balancer (NLB), HAProxy (in TCP mode).

#### Layer 7 Load Balancing (Application Layer)
- **How it works:** Fully opens the protocol envelope. Routes traffic based on the actual content of the HTTP/HTTPS request.
- *Pros:* "Smart" routing. You can route video traffic (\`/video/*\`) to high-bandwidth servers, and API traffic (\`/api/*\`) to high-CPU servers. It can terminate SSL connections, decrypting traffic before hitting your servers.
- *Cons:* Slower and vastly more CPU intensive because it must decrypt TLS and parse HTTP headers.
- *Example:* AWS Application Load Balancer (ALB), Nginx, Traefik.

### Core Routing Algorithms
How does the LB choose which server gets the next request?
1. **Round Robin:** Sequential, circular distribution (Server 1, then 2, then 3, repeat). Easiest to implement.
2. **Least Connections:** Routes the request to the server with the fewest currently active connections. Excellent for long-lived, heavy connections (like WebSockets or heavy DB queries).
3. **IP Hash (Sticky Sessions):** Hashes the client's IP address to map them consistently to exactly one server. Required if the application is poorly designed and stores session state locally in server RAM.
4. **Weighted Round Robin:** Administrators assign higher "weights" to beefy servers (getting 80% of traffic) and lower weights to weak servers (getting 20%).

<diagram>
graph TD
    Client1[Client 1] --> LB[Load Balancer]
    Client2[Client 2] --> LB
    Client3[Client 3] --> LB
    LB -->|Round Robin| S1[App Server 1]
    LB -->|Round Robin| S2[App Server 2]
    LB -->|Round Robin| S3[App Server 3]
    S1 --> DB[(Database)]
    S2 --> DB
    S3 --> DB
</diagram>

---

> **⚡ Quick Look:**
> - **Layer 4 (Transport):** Routes by IP/Port. Extremely fast, very dumb.
> - **Layer 7 (Application):** Routes by HTTP path/header. Slower, but can do smart routing (microservices) and SSL termination.
> - **Algorithms to know:** Round Robin (dumb), Least Connections (smart for variable workloads), IP Hash (when you desperately need sticky sessions).
    `
    },
    {
        id: 'cdn',
        category: 'Networking',
        title: 'Content Delivery Network (CDN)',
        summary: 'Geographically distributed network of proxy servers to deliver cached content fast.',
        content: `
## Content Delivery Network (CDN)

A Content Delivery Network (CDN) is a globally distributed network of proxy servers (Edge Locations) deployed in multiple data centers worldwide. The primary goal of a CDN is to serve content to end-users with high availability and phenomenally low latency.

### The Problem CDNs Solve
Imagine your origin application server is hosted in AWS \`us-east-1\` (Virginia, USA).
- A user sitting in Washington D.C. requests a 5MB hero image. The request travels 50 miles. Latency: **~10ms**.
- A user sitting in Sydney, Australia requests the same image. The request must traverse undersea fiber-optic cables across the entire Pacific Ocean. Physics dictates this takes time. Latency: **~250ms+**.
- Worse, if 100,000 users in Australia request the image simultaneously, your single Virginia server's bandwidth will buckle, and the server will crash.

### How CDNs Fix This
With a CDN (like Cloudflare, Akamai, or AWS CloudFront), when the Australian user requests the image, they do not connect to Virginia. They connect to a CDN Edge Server physically located in Sydney. The Edge Server has a cached copy of the image and serves it instantly (Latency: ~15ms). 

### Push vs Pull CDNs
Depending on your architecture, there are two ways to get data into the CDN:

1. **Pull CDN (Most Common):** 
   - The user requests \`image.jpg\` from the CDN. 
   - The CDN checks its cache. If missing (Cache Miss), the CDN itself fetches the image from your Origin Server, caches it locally, and serves the user.
   - Future requests for \`image.jpg\` directly hit the edge cache (Cache Hit) until the TTL (Time to Live) expires.
   - *Best for:* Dynamic web applications with massive amounts of changing content.

2. **Push CDN:**
   - The developer takes explicit responsibility for uploading (pushing) files directly to the CDN's storage servers ahead of time.
   - *Best for:* Small sites with static assets that almost never change, or pre-warming a cache for a massive scheduled event (like a new video game patch release).

### Cache Invalidation Strategies
The hardest part of a CDN is knowing when to delete stale files.
- **TTL (Time to Live):** The asset expires automatically after X hours based on the \`Cache-Control\` header.
- **Cache Busting Strategy:** The industry standard. Never update a file in place. When you release a new version of JavaScript, rename it from \`app-v1.js\` to \`app-v2.js\`. Because the filename is unique, the CDN treats it as a brand new asset, completely bypassing stale cache issues without manual invalidation requests.

<diagram>
graph TD
    UserAU[User (Australia)] --> Edge1[CDN Edge (Sydney)]
    UserUS[User (USA)] --> Edge2[CDN Edge (Virginia)]
    UserEU[User (UK)] --> Edge3[CDN Edge (London)]
    
    Edge1 -.-> |Cache Miss (1st time)| Origin[Origin Server (USA)]
    Edge2 -.-> |Cache Miss| Origin
    Edge3 -.-> |Cache Miss| Origin
    
    classDef origin fill:#fce4ec,stroke:#d81b60,stroke-width:2px;
    classDef edge fill:#e3f2fd,stroke:#1e88e5,stroke-width:2px;
    Origin:::origin
    Edge1:::edge
    Edge2:::edge
    Edge3:::edge
</diagram>

---

> **⚡ Quick Look:**
> - **Purpose:** Sits globally to cache static assets (HTML, CSS, JS, Images, Video) as close to the user's geographical location as possible.
> - **Benefits:** Slashes latency, vastly reduces the CPU/bandwidth load on your origin servers, and provides massive DDoS protection.
> - **Cache Busting:** To update a file, change its filename or add a query hash (\`style.css?v=2\`).
    `
    },

    // 3. Databases
    {
        id: 'db-sql-vs-nosql',
        category: 'Databases',
        title: 'SQL vs NoSQL',
        summary: 'When to use a relational database vs a non-relational database.',
        content: `
## SQL vs NoSQL

In system design interviews, "Which database should we use?" is the most common and critical question. Understanding the philosophical divide between Relational (SQL) and Non-Relational (NoSQL) is mandatory.

### SQL (Relational Databases)
*Exemplars: PostgreSQL, MySQL, Oracle, SQL Server.*

- **Structure:** Strict tabular data. Schemas must be explicitly defined (Columns and data types are locked in). 
- **Relationships:** Highly relational. Built around linking tables via Foreign Keys and resolving them via complex \`JOIN\` operations. Normalization is heavily pushed to reduce data redundancy.
- **Guarantees (ACID):** Strictly adheres to ACID properties (Atomicity, Consistency, Isolation, Durability). If a transaction fails midway, it correctly rolls back. Data integrity is absolute.
- **Scaling:** Traditionally scales **Vertically** (buying a bigger, more expensive server with more RAM). Scaling out (Sharding across multiple servers) is possible but notoriously complex and breaks native JOIN capabilities.
- **When to use:** Financial transactions, ERP systems, healthcare records, complex query requirements, and any system where data consistency is absolutely paramount over extreme read/write availability.

### NoSQL (Non-Relational Databases)
*Exemplars: MongoDB (Document), Cassandra (Wide-Column), Redis (Key-Value), Neo4j (Graph).*

- **Structure:** Schema-less (or schema-flexible). You can dump arbitrary JSON documents, wide-columns, or nested graphs without altering a rigid table structure.
- **Relationships:** Actively discourages JOINs. Data is heavily Denormalized (duplicated). Rather than storing a user ID and joining to a user table, you store the entire user object directly nested inside the comment object.
- **Guarantees (BASE):** Follows BASE properties (Basically Available, Soft state, Eventual consistency). To achieve massive scale, NoSQL systems sacrifice strict, instant consistency (CAP theorem).
- **Scaling:** Designed from the ground up to scale **Horizontally**. Adding 10 new servers to a Cassandra cluster is trivial; the system automatically redistributes the partitions.
- **When to use:** Rapid agile development with evolving data models, massive unstructured data scale (e.g., social media timelines), heavy write patterns (e.g., IoT sensor telemetry, logging), real-time big data pipelines.

<diagram>
graph LR
    subgraph Relational SQL
    Table1[Users Table]
    Table2[Orders Table]
    Table1 -.->|Foreign Key JOIN| Table2
    end
    
    subgraph Document NoSQL
    Doc[JSON Document]
    Doc1[{"_id": "u1", "name": "Bob", "orders": [{"id": "o1", "total": 50}]}]
    Doc --- Doc1
    end
</diagram>

---

> **⚡ Quick Look:**
> - **SQL:** Strict schema, relational JOINs, ACID transactions, vertically scaled. Use when money or absolute data integrity is on the line.
> - **NoSQL:** Flexible schema, heavily denormalized (no JOINs), BASE (eventually consistent), horizontally scaled. Use when you need massive scale, blazing throughput, or flexible data structures.
    `
    },
    {
        id: 'acid-vs-base',
        category: 'Databases',
        title: 'ACID vs BASE',
        summary: 'The contrasting data consistency models for databases.',
        content: `
## ACID vs BASE

When choosing a database in a distributed system, you are actually choosing between two entirely different philosophies regarding how data integrity is handled.

### ACID (The Relational/SQL Philosophy)
ACID prioritizes **Safety and Consistency** above all else.
* **Atomicity:** The "All or Nothing" rule. A transaction containing 5 database queries either fully completes, or entirely rolls back if the 5th query fails. There is no partial state.
* **Consistency:** Any transaction will bring the database from one valid state to another valid state, abiding by all defined rules (like unique constraints, cascading deletes, and triggers).
* **Isolation:** If 100 people try to book the same airline seat at the exact same millisecond, unfinished transactions are hidden/isolated from each other (usually via Row Locking or MVCC). Only one transaction succeeds.
* **Durability:** Once a transaction is "committed", the database utilizes a Write-Ahead Log (WAL). Even if someone unplugs the database server a millisecond later, the data is permanently saved and will recover upon boot.

*The Tradeoff:* Strict ACID compliance requires heavy coordination (locks, blockages, disk syncing). It is relatively slow and fights heavily against distributed horizontally-scaled architecture.

### BASE (The Distributed/NoSQL Philosophy)
In heavily distributed systems spanning the globe, strict ACID checking is impossibly slow and vulnerable to network partitions (see CAP Theorem). BASE prioritizes **Extreme Availability**.
* **Basically Available:** The system guarantees availability. It promises a read/write response, even if the internal nodes are struggling or partitioned.
* **Soft State:** The state of the system is fluid. Because data is being replicated asynchronously in the background across data centers, the state might change even without explicit user input as nodes sync up.
* **Eventual Consistency:** The critical tradeoff. Given enough time without new writes, all nodes will become consistent. If User A updates their profile picture, Node 1 accepts the write. For a few seconds, User B reading from Node 2 might still see the old picture. *Eventually*, Node 1 syncs with Node 2.

*The Tradeoff:* You gain massive uptime, partition tolerance, and blazing speed, but the developer must design the UI gracefully to handle slightly stale data temporarily.

<diagram>
graph TD
    subgraph ACID Transaction
    Start[Begin] --> Node1[Deduct $100 from A]
    Node1 --> Node2[Add $100 to B]
    Node2 --> Commit[Commit]
    Node1 -.->|Crash| Rollback[Rollback All]
    Node2 -.->|Crash| Rollback
    end
    
    subgraph BASE Eventual Consistency
    US[User Posts Picture] --> Server1[Node A accepts write]
    Server1 -.-> |Background Sync| Server2[Node B]
    Server1 -.-> |Background Sync| Server3[Node C]
    Server2 -- Eventually gets pic --> Client[Client reads Node B]
    end
</diagram>

---

> **⚡ Quick Look:**
> - **ACID:** Defensive, rigid, reliable. Ensures data operations are perfect and sequential. Required for money transfers.
> - **BASE:** Optimistic, fluid, highly available. Acknowledges that in global distributed systems, temporary data staleness (eventual consistency) is acceptable in exchange for infinite scalability.
    `
    },
    {
        id: 'sharding',
        category: 'Databases',
        title: 'Database Sharding',
        summary: 'The process of splitting up large databases into smaller, more easily managed parts.',
        content: `
## Database Sharding

When a relational database becomes too large to fit on a single high-end physical machine (vertically scaled out), or when the write-QPS (Queries Per Second) overwhelms the disk I/O, we must implement Sharding.

### What is Sharding?
Sharding is a specific type of database partitioning that separates extremely large datasets into smaller, faster, more easily managed parts called "Data Shards." It is the act of bringing **Horizontal Scaling** to the database tier by distributing rows of a table across multiple distinct physical database servers.

### Defining the Routing Logic (The Shard Key)
If you have 4 distinct database servers (shards), and a backend API wants to look up User ID \`12345\`, how does it know which of the 4 servers to query? You must define a **Shard Key** and a routing algorithm.

#### 1. Range Based Sharding
Data is divided based on contiguous ranges of the shard key.
- *Example:* Shard 1 holds Users with IDs 1–100,000. Shard 2 holds Users 100,001–200,000. Or, Shard 1 holds users in Zip Code 10000, Shard 2 holds 20000.
- *Problem:* Extreme vulnerability to **Data Hotspots**. If you shard by date, Shard 1 (last year's data) will sit idle, while Shard 4 (today's data) will be crushed by 100% of the active traffic.

#### 2. Algorithmic / Hash Based Sharding
A hash function is applied to the Shard Key, and modulo arithmetic determines the shard (e.g., \`hash(user_id) % 4\`). 
- *Pros:* This guarantees a mathematically perfect, even distribution of data across all shards, completely eliminating hotspots.
- *Problem:* What happens when you scale up and add a 5th database server? The modulo changes from \`% 4\` to \`% 5\`. This means almost every single record now computes to a different shard. You must undergo a massive "Resharding" migration, taking the database down.
- *Solution:* **Consistent Hashing** algorithm massively mitigates the resharding problem.

#### 3. Directory Based Sharding
A completely separate lookup table (a routing or metadata service) explicitly maintains a map of which shard holds which specific key.
- *Pros:* Complete control. You can dynamically move a "heavy" user to a private shard.
- *Cons:* The lookup table itself becomes a single point of failure and bottleneck for every query.

### The Pain of Sharding
In interviews, you must mention why sharding is a last resort:
- **Resharding:** Rebalancing data is operationally terrifying.
- **The "Celebrity" Problem:** If you shard by User ID, and User ID "Justin Bieber" has 100 million followers constantly reading his data, his shard will melt down while others sit idle.
- **No More JOINs:** You cannot efficiently perform an SQL JOIN between a table on Shard 1 and a table on Shard 2. You have to denormalize data extensively.

<diagram>
graph TD
    App[Application Backend] --> Hash{Hash<br>userID % 3}
    Hash -->|Result = 0| DB0[(Shard A<br>Users 0, 3, 6)]
    Hash -->|Result = 1| DB1[(Shard B<br>Users 1, 4, 7)]
    Hash -->|Result = 2| DB2[(Shard C<br>Users 2, 5, 8)]
</diagram>

---

> **⚡ Quick Look:**
> - **Definition:** Distributing rows of one massive table across multiple physical database servers.
> - **Shard Key:** The column (like \`user_id\`) used to calculate which server holds the data.
> - **Caveat:** It breaks complex \`JOIN\` operations, making SQL DBs behave more like NoSQL DBs. It requires painful maintenance during scaling events.
    `
    },

    // 4. Caching
    {
        id: 'caching-strategies',
        category: 'Caching',
        title: 'Caching Strategies',
        summary: 'How to use caching effectively to reduce database load and improve read latency.',
        content: `
## Caching Strategies

A cache is a fast, temporary, in-memory (RAM) data store that reduces the need to access slower underlying storage (Disk arrays or complex algorithmic computations). While initializing a Redis container is easy, executing proper cache invalidation strategies is notoriously complex.

### Caching Layers in Architecture
1. **Client/Browser Cache:** Storing static assets or heavy API responses directly on the user's device.
2. **CDN Cache:** Caching static assets at edge locations globally.
3. **Application Cache (e.g., Redis, Memcached):** Sitting between the monolithic app server and the primary database.

### The Read/Write Strategies

#### 1. Cache Aside (Lazy Loading)
This is the most common industry standard.
- **Read Logic:** The app queries the cache. If found (Cache Hit), return. If not found (Cache Miss), the app queries the Database, writes the result to the cache, and then returns.
- **Write Logic:** The app writes data directly to the Database, and then immediately *deletes* or *updates* the corresponding key in the cache.
- *Pros:* The cache only contains data that users are actively requesting, saving RAM.
- *Cons:* The very first request for an item always incurs a cache miss latency penalty. If the "write" step fails to delete the cache key, the system permanently serves stale data.

#### 2. Write-Through
- **Write Logic:** The application writes data exclusively to the Cache. The Cache then *synchronously* writes the data to the Database. The operation isn't complete until the DB confirms.
- *Pros:* Data in the cache is guaranteed to be 100% consistent with the database. Reads are blazingly fast right out of the gate.
- *Cons:* Write latency is significantly higher because it must endure two network hops (App -> Cache -> DB).

#### 3. Write-Back (Write-Behind)
- **Write Logic:** The application writes data to the Cache, and the cache *immediately* returns a success response to the app. In the asynchronous background, the Cache batches the data and syncs it to the Database.
- *Pros:* Extreme write performance speeds. Excellent for write-heavy workloads (e.g., tracking video view counts, logging real-time telemetry).
- *Cons:* High risk factor. If the Redis server crashes or loses power before the background thread syncs the data to disk, the data is permanently lost.

### Cache Eviction Policies
Because RAM is expensive and finite, you need rules for what to delete when the server gets to 100% capacity:
- **LRU (Least Recently Used):** (Most popular). Evict the item that hasn't been requested for the longest period of time.
- **LFU (Least Frequently Used):** Evict the item with the lowest total access count.
- **FIFO (First In, First Out):** Evict the oldest item written to the cache, regardless of how often it is accessed.

<diagram>
sequenceDiagram
    participant App
    participant Cache as Redis Cache
    participant DB as Main Database
    
    App->>Cache: 1. Get User Profile?
    alt Cache Hit
        Cache-->>App: 2. Return User Profile (Fast)
    else Cache Miss
        Cache-->>App: 2. Not Found
        App->>DB: 3. Fetch User Profile
        DB-->>App: 4. Return User Profile (Slow)
        App->>Cache: 5. Store User Profile in Cache
    end
</diagram>

---

> **⚡ Quick Look:**
> - **Cache Aside (Read-Heavy):** Query cache -> if miss -> query DB -> update cache. Safest, most common approach.
> - **Write-Through (Consistent):** Ensures cache and DB are perfectly synced, but adds delay to writes.
> - **Write-Back (Write-Heavy):** Extreme speed, writes to cache and syncs to DB later. Risky if cache crashes.
> - **Eviction:** Use LRU to delete dusty data when memory runs out.
    `
    },

    // 5. Message Queues & Streaming
    {
        id: 'message-queues',
        category: 'Queues & Streaming',
        title: 'Message Queues vs Pub/Sub',
        summary: 'Decoupling services asynchronously using RabbitMQ, SQS, and Kafka.',
        content: `
## Asynchronous Communication

In a mature microservices architecture, synchronous communication (Service A connecting via HTTP REST to Service B) is dangerous. It tightly couples services. If the Email Service is down, and the Checkout Service waits via HTTP, the Checkout Service times out and the user fails to check out. 

We solve this by decoupling services using asynchronous queues and event buses.

### Message Queues (Point-to-Point Task Distribution)
*Exemplars: RabbitMQ, Amazon SQS, ActiveMQ.*

- **How it works:** A Producer pushes a defined message (a task) into a queue. A Consumer (a background worker) polls the queue, reads the message, processes it, and acknowledges (ACKs) it. Once acknowledged, **the message is permanently deleted from the queue**.
- **The Power of Workers:** If you have 10,000 PDF generation tasks in the queue, you can spin up 50 parallel worker nodes. The Queue acts as a load balancer, ensuring each PDF task is processed exactly once by exactly one worker.
- **Use Case:** Heavy background processing. Sending transactional emails, processing uploaded videos, generating heavy reports.

### Pub/Sub & Event Streaming (Broadcast)
*Exemplars: Apache Kafka, Amazon SNS, Google Cloud Pub/Sub.*

- **How it works:** A Publisher generates an event and sends it to a "Topic" (e.g., \`OrderPlacedTopic\`). Instead of one worker taking the message, multiple independent Subscribers (consumers) "listen" to that topic. The message is broadly **broadcast (Fanout)** to ALL subscribers simultaneously.
- **The Event Log (Kafka):** Unlike standard queues, Event Streaming platforms like Kafka act as a persistent, durable log. Messages are *retained* for a configurable period (like 7 days), whether they are read or not. This allows new consumers to "replay" the history of the system from the beginning of time to rebuild state.
- **Use Case:** Event-driven architecture. A user completes payment. The \`PaymentSuccess\` event goes to Kafka. Automatically, the Billing service reads it (to make a PDF invoice), the Inventory service reads it (to deduct stock), and the Shipping service reads it (to print a label). All operate independently from a single event.

<diagram>
graph LR
    subgraph Message Queue point-to-point
    P1[Producer] --> Q[(Queue)]
    Q --> W1[Worker 1]
    Q --> W2[Worker 2]
    end
    
    subgraph Pub/Sub Broadcast
    Pub[Publisher] --> T[(Topic / Kafka)]
    T --> S1[Subscriber A: Billing]
    T --> S2[Subscriber B: Inventory]
    T --> S3[Subscriber C: Shipping]
    end
</diagram>

---

> **⚡ Quick Look:**
> - **Message Queue (RabbitMQ/SQS):** Point-to-Point. One task, processed by exactly one worker, then deleted forever. Use for load-balancing heavy background tasks.
> - **Pub/Sub (Kafka/SNS):** Broadcast. One event, blasted out to multiple independent microservices. Messages can be persisted to replay history. Use for true event-driven choreography.
    `
    },

    // 6. Security & Consensus
    {
        id: 'consistent-hashing',
        category: 'Algorithms & Consensus',
        title: 'Consistent Hashing',
        summary: 'A hashing technique crucial for distributed caching and database sharding.',
        content: `
## Consistent Hashing

Consistent hashing is an elegant algorithmic routing technique crucial for managing distributed hash tables (like a cluster of Redis servers) and defining routing layers for Database Sharding. It famously powers systems like Amazon DynamoDB and Apache Cassandra.

### The Catastrophe of Simple Hashing
To evenly distribute data, developers use a hash function modulus. 
Say you have 4 Cache Servers (N=4). To decide where to store \`KeyA\`, you do: \`hash(KeyA) % 4\`.
This mathematically perfect distribution works wonderfully until a scaling event occurs:
- **Server Crash:** A server goes down. N becomes 3. The formula is now \`hash(KeyA) % 3\`.
- **Scaling Up:** You add a 5th server for load. The formula is now \`hash(KeyA) % 5\`.

In both scenarios, the modulo arithmetic changes for *every single key*. Almost EVERY piece of data currently cached will now hash to a different, incorrect server. You experience a massive **cache miss storm** across your entire network that will instantly choke and crash your origin databases.

### The Consistent Hashing Solution
Instead of mapping data to specific server integer slots arrays, we map both the **Servers** and the **Data Keys** onto a massive abstraction called a "Hash Ring."

1. **The Ring:** Imagine a conceptual circle spanning from 0 to $2^{160}-1$.
2. **Place the Servers:** Hash the IP addresses of your cache servers through a strong algorithm (like SHA-1) and place them at their corresponding angle on the ring.
3. **Place the Data:** Hash the Data Key, which places it randomly somewhere on the ring.
4. **The Routing Rule:** To find which server holds the data, start at the data's position on the ring and **travel clockwise** until you hit the first server node.

### Why it solves the scaling problem smoothly
- **Adding a node:** If we inject Server E between B and C on the ring, only the data keys falling numerically between B and E are affected. Data on other servers remains completely untouched.
- **Removing a node:** If Server B catches fire, only the specific keys that belonged to B are remapped (they "fall forward" onto Server C).
- *Result:* When N changes, only roughly **\`K/N\` keys need to be remapped** (where K is total keys, N is total servers). If you have 10 servers and 1M keys, adding an 11th server requires moving only ~90k keys instead of re-hashing all 1M.

### Dealing with Hotspots (Virtual Nodes)
If the ring is randomly populated, physical servers might have drastically uneven spacing, leading to uneven data loads. To fix this, you generate **Virtual Nodes**. Instead of placing Server A once, you hash its IP with an index (e.g., \`ServerA_1\`, \`ServerA_2\`...\`ServerA_100\`) and scatter all 100 virtual nodes across the ring, guaranteeing perfectly uniform distribution.

<diagram>
graph TD
    subgraph Non-Consistent Hashing
    K1[Key % 4] --> S1[Server 1]
    K2[Key % 5] --> S2[Server 2 - ALL DATA MOVES]
    end
    
    subgraph Consistent Hashing Ring
    Ring((Hash Ring 0-360))
    Ring --- SA((Server A))
    Ring --- SB((Server B))
    Ring --- SC((Server C))
    DataA[Data Key hash] -.-> |Clockwise| SA
    end
</diagram>

---

> **⚡ Quick Look:**
> - **The Goal:** Prevent a massive cache-miss meltdown when servers are added or removed to a horizontally scaling distributed system.
> - **The Method:** Map both servers and data to a conceptual ring. Data travels clockwise to find its home server.
> - **The Result:** Instead of migrating 100% of data during a scaling event, you only migrate \`1/N\`% of data.
> - **Optimization:** Use Virtual Nodes to ensure the ring spacing is perfectly uniform.
    `
    },

    // 7. API Design & Security
    {
        id: 'rate-limiting',
        category: 'Networking',
        title: 'Rate Limiting Algorithms',
        summary: 'Preventing server overload and DDoS attacks by strictly throttling API requests.',
        content: `
## Rate Limiting

Rate limiting restricts the number of requests a client can make to a server over a specified period. It is the fundamental defense mechanism against DDoS attacks, brute-force hacking, and accidental self-inflicted traffic spikes.

### Why Rate Limit?
1. **Prevent Resource Starvation:** Stop a single buggy script from consuming 100% of your database CPU, starving all other users.
2. **Cost Control:** If you use third-party APIs (like OpenAI) that charge per request, rate limiting prevents massive unexpected bills.
3. **Security:** Prevent credential stuffing/brute force attacks on the login endpoint.

### Common Throttling Algorithms

#### 1. Token Bucket
- **How it works:** Imagine a bucket containing 10 tokens. Every minute, a background cron job adds 2 tokens to the bucket, up to the maximum capacity of 10. When a user makes an API request, they "spend" one token. If the bucket is empty, the request is dropped (HTTP 429 Too Many Requests).
- **Pros:** Allows for sudden bursts of traffic (up to the bucket capacity), while strictly enforcing a long-term steady average rate.
- **Example:** Amazon API Gateway.

#### 2. Leaky Bucket
- **How it works:** Requests enter a bucket at an uncontrolled rate from the top. However, the bucket "leaks" (processes requests) out of the bottom at a strict, perfectly constant rate using a First-In-First-Out (FIFO) queue. If water pours in faster than it leaks out, the bucket overflows, and requests drop.
- **Pros:** Smooths out bursty traffic into a perfectly steady, predictable stream. Protects old, fragile backend systems from sudden spikes.

#### 3. Fixed Window Counter
- **How it works:** Time is divided into exact windows (e.g., 10:00:00 to 10:01:00). A counter increments per request. If it hits the limit (e.g., 100), block requests until the window ends at 10:01:00, then reset to 0.
- **Cons:** *The boundary spike problem.* A malicious user can send 100 requests at 10:00:59, and another 100 requests at 10:01:01. They bypassed your "100 per minute" rule by doing 200 requests in 2 seconds.

#### 4. Sliding Window Log / Counter
- **How it works:** Fixes the boundary problem by keeping an exact timestamp log of every request, or keeping a rolling weighted average of previous windows to ensure strict mathematical limits across any arbitrary 60-second slice of time.

<diagram>
graph TD
    subgraph Token Bucket Algorithm
    Cron[Refiller: Adds 2/min] --> Bucket((Bucket<br/>Capacity: 10))
    Bucket --> |Has Tokens| Pass[Request Allowed]
    Bucket --> |Empty| Block[HTTP 429 Blocked]
    end
</diagram>

---

> **⚡ Quick Look:**
> - **Purpose:** Protects backend APIs from overuse and malicious attacks.
> - **Token Bucket:** Allows traffic bursts but maintains an average rate.
> - **Leaky Bucket:** Forces traffic into a perfectly smooth, constant rate.
> - **Fixed Window:** Simple to build in Redis, but vulnerable to spikes at the very start/end of the minute.
    `

    },
    // 8. Advanced Paradigms
    {
        id: 'api-paradigms',
        category: 'Networking',
        title: 'WebSockets vs SSE vs Long Polling',
        summary: 'How to push real-time data from the server to the client without aggressive polling.',
        content: `
## Real-Time Data Transfer

When a client needs real-time updates (like a chat app, live sports scores, or a stock ticker), standard HTTP requests (where the client asks the server and the server responds) fall short. A server cannot naturally "push" new data to a client over standard HTTP.

### 1. HTTP Short Polling (The Bad Way)
The client simply sends an AJAX request to the server every 1 second: *"Any new messages? No. Any new messages? No."*
- *Problem:* This creates massive network overhead. 99% of requests return empty-handed, needlessly occupying server CPU, memory, and database connections.

### 2. HTTP Long Polling
The client sends a request. If the server has no new data, it **holds the connection open** (does not respond) until new data arrives. Once data arrives, the server responds, and the client immediately opens a new long-polling connection.
- *Pros:* Works strictly over standard HTTP. Universally supported by every firewall and proxy.
- *Cons:* Heavy resource consumption. The server must keep thousands of hung HTTP connections open in memory simultaneously. If a user receives a burst of 10 messages, it triggers 10 full HTTP handshakes.

### 3. WebSockets
WebSockets upgrade a standard HTTP connection into a **persistent, full-duplex, bi-directional TCP connection**. 
Both the client and the server can push raw data frames to each other at any time, instantly, without the overhead of HTTP headers.
- *Use Case:* Multiplayer browser gaming, fast-paced chat applications (Discord), collaborative editing (Google Docs).
- *Pros:* Extremely low latency. Real bi-directional communication.
- *Cons:* Stateful. Because the TCP connection is persistent, "Sticky Sessions" or a central Pub/Sub cache (like Redis) is required to route messages to the exact server holding the user's socket. Harder to automatically load-balance.

### 4. Server-Sent Events (SSE)
SSE establishes a single, long-lived HTTP connection, but it is strictly **Uni-Directional** (Server -> Client). The server streams data down to the client as plain text events over time.
- *Use Case:* Live stock tickers, Twitter/Facebook feed updates, live sports scores.
- *Pros:* Simpler than WebSockets (it uses standard HTTP). Has built-in reconnection logic and event IDs.
- *Cons:* The client cannot send data back over this connection (they must make standard POST requests to send messages). 

<diagram>
sequenceDiagram
    participant C as Client
    participant S as Server
    
    %% Long Polling
    Note over C,S: Long Polling
    C->>S: GET /messages (Hangs...)
    Note right of S: Data Arrives!
    S-->>C: 200 OK [Messages]
    C->>S: GET /messages (Hangs again...)
    
    %% WebSockets
    Note over C,S: WebSockets
    C->>S: HTTP GET (Upgrade: websocket)
    S-->>C: 101 Switching Protocols
    Note over C,S: Persistent TCP Connection established
    C->>S: Frame: "Hello Server"
    S-->>C: Frame: "Hello Client"
    S-->>C: Frame: "More Data"
</diagram>
        `
    },
    {
        id: 'bloom-filters',
        category: 'Algorithms & Consensus',
        title: 'Bloom Filters',
        summary: 'A hyper-efficient probabilistic data structure that tells you if an item is definitely NOT in a set.',
        content: `
## Probabilistic Data Structures: The Bloom Filter

Imagine you are designing Google Chrome's "Malicious URL Warning" system. You have a database of 10 billion malicious URLs. When a user types a URL, how do you verify if it's malicious locally on their laptop in 1 millisecond without downloading a 100GB database? 

You use a **Bloom Filter**.

### What is a Bloom Filter?
A Bloom Filter is a wildly space-efficient probabilistic data structure. It is designed to answer one question incredibly fast: *"Is this item in the set?"*

Its magic lies in its probabilistic nature:
- It can tell you with **100% certainty that an item is NOT in the set**.
- It can tell you with **high probability that an item MIGHT BE in the set** (False Positives are possible).

### How it Works
At its core, a Bloom Filter is simply an array of bits (0s and 1s), initially all set to 0.

**Adding an Item:**
1. You take the string "bad-site.com".
2. You pass it through 3 different hash functions (e.g., MD5, SHA1, Murmur).
3. The hashes output 3 array index numbers: 4, 12, and 28.
4. You flip the bits at indexes 4, 12, and 28 in the array to \`1\`.

**Checking an Item:**
1. A user visits "google.com".
2. You run "google.com" through the *exact same* 3 hash functions.
3. They output indexes 2, 8, and 12.
4. You look at the array. Index 12 is \`1\`, but Indexes 2 and 8 are \`0\`.
5. Because at least one bit is \`0\`, you know with absolute, mathematical certainty that "google.com" was **never added** to the filter. It is safe.

**The False Positive Catch:**
1. A user visits "innocent.com".
2. The hashes output 4, 12, and 28.
3. You check the array. All three bits are \`1\` (because "bad-site.com" flipped them earlier).
4. The filter says: *"This URL is probably malicious."* (This is a false positive).

### The System Design Rescue
In system design, Bloom Filters are used as an ultra-fast **first line of defense** to prevent expensive disk/database lookups.

1. App checks Bloom Filter for "username_taken?".
2. If filter says NO -> Username is definitely free. Write to DB instantly.
3. If filter says PROBABLY YES -> Do the slow, expensive DB query to double-check if it's a false positive.
Because 99% of requests hit the "NO" condition, you save 99% of database I/O!

<diagram>
graph TD
    classDef bits fill:#f9f,stroke:#333,stroke-width:4px;
    classDef empty fill:#fff,stroke:#333,stroke-width:2px;
    
    A[Add 'bad.com'] --> H1(Hash 1 : idx 2)
    A --> H2(Hash 2 : idx 5)
    A --> H3(Hash 3 : idx 8)
    
    H1 --> B2((1)):::bits
    H2 --> B5((1)):::bits
    H3 --> B8((1)):::bits
    
    subgraph Bit Array representing 10 Billion URLs in 2MB
        B0((0)):::empty
        B1((0)):::empty
        B2
        B3((0)):::empty
        B4((0)):::empty
        B5
        B6((0)):::empty
        B7((0)):::empty
        B8
    end
</diagram>
        `
    },
    {
        id: 'event-sourcing',
        category: 'Architecture & Scaling',
        title: 'Event Sourcing & CQRS',
        summary: 'Storing the history of state changes as an immutable sequence of events, rather than just the current state.',
        content: `
## Event Sourcing

Traditional databases store **current state**. If your bank balance is $500, and you withdraw $100, the database overwrites the "500" row with "400". The old data is destroyed. 

**Event Sourcing** instead stores **an immutable sequence of events**. You do not store the balance. You store: \`AccountOpened(0)\`, \`Deposited(500)\`, \`Withdrew(100)\`. 

### Why Event Sourcing?
1. **Perfect Audit Trail:** Because you never overwrite data, you have a cryptographically perfect history of every action that ever occurred in the system. Crucial for banking, healthcare, and accounting.
2. **Time Travel:** Need to know what a user's account looked like exactly on Jan 14th at 2PM? Simply "replay" all events from the beginning of time until that exact timestamp.
3. **Debugging:** If a catastrophic bug corrupted data, you fix the bug, drop the current state, and replay the event log from scratch to perfectly rebuild the database.

### The CQRS Pattern
Event Sourcing is almost universally paired with **CQRS (Command and Query Responsibility Segregation)**. 

Because querying "What is the current balance?" by replaying 10,000 events on the fly is impossibly slow, you split the architecture in half:
- **Command (Write) Side:** Receives actions from the user, validates them, and strictly appends the immutable Events into an Event Store (like Apache Kafka or EventStoreDB).
- **Query (Read) Side:** A background worker listens to those events, calculates the new final state, and writes the *denormalized current state* into a completely separate database (like Redis or PostgreSQL) highly optimized for lightning-fast reads.

<diagram>
graph TD
    UI[Client UI]
    
    subgraph Write Side (Command)
        UI -- 1. Submit Order --> API_C[Command API]
        API_C -- 2. Validate & Append --> ES[(Event Store: Kafka / EventStore)]
        ES --> O1[Event: OrderCreated]
        ES --> O2[Event: ItemAdded]
    end
    
    subgraph Read Side (Query)
        O1 -. 3. Async Broadcast .-> Proj[Projection Worker]
        O2 -. 3. Async Broadcast .-> Proj
        Proj -- 4. Update Current State --> DB[(Read DB: Redis / Mongo)]
        UI -- 5. Get Current Order --> API_Q[Query API]
        API_Q -- 6. Sub-millisecond lookup --> DB
    end
</diagram>
        `
    },

    // 9. Database Internals
    {
        id: 'db-partitioning-vs-sharding',
        category: 'Databases',
        title: 'Partitioning vs Sharding',
        summary: 'The subtle but critical distinction between splitting data within a single database vs across multiple independent servers.',
        content: `
## Database Partitioning vs. Sharding

These two terms are frequently confused in interviews, but they represent fundamentally different architectural strategies for managing large datasets.

### Partitioning (Single Server)

Partitioning splits a single large table into smaller, more manageable pieces **within the same database server**. The database engine itself handles the routing transparently — your application code does not change.

#### Horizontal Partitioning (Range / List)
Rows are split based on a column value. Example: A "Sales" table is partitioned by year. Partition 1 holds 2020 data, Partition 2 holds 2021, etc. When you query "SELECT * FROM sales WHERE year = 2023", the database only scans Partition 4, completely skipping the other 3 — a massive speedup called **Partition Pruning**.

#### Vertical Partitioning
Columns are split into separate physical tables. Example: A "Users" table has 50 columns but 90% of queries only need name, email, and avatar. You split the rarely-used columns (bio, preferences, analytics_data) into a separate "UserDetails" partition. Hot queries read less data from disk.

### Sharding (Multiple Servers)

Sharding distributes data **across multiple independent database servers** (shards). Each shard is a completely separate database process, potentially running on different machines in different data centers.

The critical difference: the **application or a routing layer** must know which shard to query. The database itself has no knowledge of the other shards.

### The Key Distinction

| Feature | Partitioning | Sharding |
|---|---|---|
| **Scope** | Single database server | Multiple independent servers |
| **Routing** | Database handles transparently | Application/proxy must route |
| **JOINs** | Work normally across partitions | Broken across shards |
| **Scaling** | Limited by single server resources | Theoretically infinite |
| **Complexity** | Low (built into most RDBMS) | Very High (application-level logic) |

<diagram>
graph TD
    subgraph Partitioning (Single Server)
        DB[(PostgreSQL Server)]
        DB --> P1[Partition 2020]
        DB --> P2[Partition 2021]
        DB --> P3[Partition 2022]
    end

    subgraph Sharding (Multiple Servers)
        Router{Shard Router} --> S1[(Shard 1: Users A-M)]
        Router --> S2[(Shard 2: Users N-Z)]
    end
</diagram>

---

> **⚡ Quick Look:**
> - **Partitioning:** Splitting data within ONE database server. Transparent to the app. JOINs still work.
> - **Sharding:** Splitting data across MANY servers. Application must route queries. JOINs break.
> - *Interview Tip:* Always try **Partitioning first** before reaching for sharding. It is dramatically simpler.
    `
    },
    {
        id: 'db-replication',
        category: 'Databases',
        title: 'Database Replication',
        summary: 'Copying data across multiple servers for high availability, fault tolerance, and read scaling.',
        content: `
## Database Replication

Replication is the practice of maintaining exact copies of the same database across multiple servers (Replicas). It is the bedrock of **High Availability** and **Read Scaling**.

### Single-Leader (Master-Slave) Replication

The most common pattern. One server is designated the **Primary (Leader)**. All writes go to the Leader. The Leader then streams its changes (via a Write-Ahead Log / WAL) to one or more **Read Replicas (Followers)**.

- **Reads:** Distributed across all replicas via a Load Balancer. This massively scales read throughput.
- **Writes:** Only hit the Leader. This is the bottleneck.
- **Failover:** If the Leader dies, a Replica is promoted to the new Leader (automatic with tools like Patroni for PostgreSQL, or built-in with MongoDB Replica Sets).

### Multi-Leader Replication

Multiple nodes can accept writes simultaneously. Used in **multi-data-center** deployments where you need a writable database in each geographic region to reduce latency.

- **The Nightmare: Write Conflicts.** If User A changes their email on the US node and User B changes the same email on the EU node at the same time, you have a conflict. Resolution strategies include Last-Write-Wins (LWW) or custom merge functions.

### Leaderless Replication (Quorum)

No designated leader. Any node can accept reads and writes (used by Cassandra and DynamoDB).

- Writes are sent to multiple nodes simultaneously. The write is confirmed when a **Quorum** (majority) of nodes acknowledge it.
- **Read Repair:** If a read hits a stale replica, the system detects the version mismatch and silently updates the stale node in the background.

<diagram>
graph TD
    subgraph Single-Leader Replication
        Write[App Write] --> Leader[(Primary DB)]
        Leader -- WAL Stream --> R1[(Read Replica 1)]
        Leader -- WAL Stream --> R2[(Read Replica 2)]
        Read[App Read] --> LB{Load Balancer}
        LB --> R1
        LB --> R2
    end
</diagram>

---

> **⚡ Quick Look:**
> - **Single-Leader:** Simplest. One writer, many readers. Covers 90% of use cases.
> - **Multi-Leader:** For geo-distributed writes. Beware of write conflicts.
> - **Leaderless:** Maximum availability. Any node reads/writes. Uses quorum consensus.
    `
    },

    // 10. Resilience Patterns
    {
        id: 'api-gateway',
        category: 'Architecture & Scaling',
        title: 'API Gateway Pattern',
        summary: 'A single entry point for all client requests that handles routing, authentication, rate limiting, and aggregation.',
        content: `
## The API Gateway Pattern

In a microservices architecture, you might have 30 internal services (User, Billing, Inventory, Search, etc.). If your mobile app had to know the IP address and API contract of each individual service, it would be a nightmare of tight coupling and network chatter.

An **API Gateway** (like Kong, AWS API Gateway, or Netflix Zuul) sits at the front door and acts as a smart reverse proxy.

### Core Responsibilities

1. **Request Routing:** The client sends ALL requests to a single URL (api.myapp.com). The Gateway inspects the path and routes /users to the User Service, /billing to the Billing Service, etc.
2. **Authentication & Authorization:** Instead of every microservice independently verifying JWT tokens, the Gateway does it once at the edge. If the token is invalid, the request is rejected before it ever touches an internal service.
3. **Rate Limiting:** The Gateway enforces traffic quotas per API key, preventing abuse and protecting downstream services.
4. **Response Aggregation (BFF - Backend for Frontend):** A mobile client's "Dashboard" screen might need data from User Service, Notification Service, AND Activity Service. Instead of 3 separate network calls from the phone, the Gateway makes the 3 internal calls in parallel and returns a single, merged JSON response.
5. **Protocol Translation:** The client speaks HTTPS/REST, but internal services might communicate via gRPC or GraphQL. The Gateway handles the translation.

<diagram>
graph TD
    Mobile[Mobile App] --> GW[API Gateway]
    Web[Web App] --> GW

    GW --> |/users| US[User Service]
    GW --> |/billing| BS[Billing Service]
    GW --> |/search| SS[Search Service]
    GW --> |/notifications| NS[Notification Service]

    GW -.-> Auth[Auth: Verify JWT]
    GW -.-> RL[Rate Limiter]
</diagram>

---

> **⚡ Quick Look:**
> - **Purpose:** Single entry point for all clients. Handles auth, routing, rate limiting, and response aggregation.
> - **Key Benefit:** Decouples clients from internal service topology. You can refactor or split services without the client ever knowing.
> - *Interview Tip:* Always include an API Gateway in any microservices system design diagram.
    `
    },
    {
        id: 'circuit-breaker',
        category: 'Architecture & Scaling',
        title: 'Circuit Breaker Pattern',
        summary: 'A resilience pattern that prevents cascading failures by stopping calls to an unhealthy downstream service.',
        content: `
## The Circuit Breaker Pattern

In a microservices architecture, Service A calls Service B, which calls Service C. If Service C goes down and starts timing out, Service B's threads pile up waiting for C. Service B slows down. Service A's threads pile up waiting for B. Service A crashes. This is a **Cascading Failure** — one broken microservice topples the entire system like dominoes.

The **Circuit Breaker** pattern (popularized by Netflix Hystrix, now Resilience4j) prevents this.

### How it Works (The Electrical Analogy)

The Circuit Breaker wraps every outgoing network call and exists in one of three states:

1. **CLOSED (Normal):** Requests flow freely to the downstream service. The breaker monitors the failure rate in the background.
2. **OPEN (Tripped):** If the failure rate exceeds a threshold (e.g., 50% of the last 20 requests failed), the breaker **trips open**. All subsequent requests are **immediately rejected** with a fallback response (e.g., a cached value or a friendly error), without even attempting to call the dead service. This frees up threads and prevents cascade.
3. **HALF-OPEN (Testing):** After a configurable timeout (e.g., 30 seconds), the breaker allows a single "test" request through. If it succeeds, the breaker resets to CLOSED. If it fails, it stays OPEN.

### Related Patterns

- **Retry with Exponential Backoff:** If a call fails, wait 1s, retry. If it fails again, wait 2s, retry. Then 4s, 8s, etc. Add random "jitter" so 1000 clients don't all retry at the exact same second.
- **Bulkhead Pattern:** Isolate resource pools. Give the "Payment Service" calls their own dedicated thread pool. If Payment hangs, it only exhausts its own pool and does not consume threads allocated for the "User Service" calls.
- **Timeout Pattern:** Never make a network call without a strict timeout. A missing timeout is a guaranteed cascading failure waiting to happen.

<diagram>
graph LR
    A[Service A] --> CB{Circuit Breaker}

    CB -->|CLOSED: Allow| B[Service B]
    CB -->|OPEN: Reject| Fallback[Return Cached/Default Value]

    B -->|Success| CB
    B -->|Failure Rate > 50%| CB
    CB -->|After 30s| HalfOpen[HALF-OPEN: Test 1 Request]
    HalfOpen -->|Success| CB
    HalfOpen -->|Fail| CB
</diagram>

---

> **⚡ Quick Look:**
> - **Problem:** One dead microservice causes thread starvation and cascading failures across the entire system.
> - **Solution:** Wrap calls in a Circuit Breaker that trips open after repeated failures, returning fast fallbacks instead of hanging.
> - *Interview Tip:* Always mention Circuit Breakers, Retries with Backoff, and Timeouts when designing any microservices architecture.
    `
    }
];
